import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { updateProfile } from '../api/client';
import {
  colors,
  applyTheme,
  themeFromConfig,
  THEME_PRESETS,
  DEFAULT_THEME,
  type ThemeColors,
} from '../constants/colors';
import { ArrowLeft, Check } from 'lucide-react';

const PRESET_NAMES: Record<string, string> = {
  default: 'Snagg Light',
  snaggDark: 'Snagg Dark',
  midnight: 'Midnight',
  sunset: 'Sunset',
  forest: 'Forest',
  snaggPinkDark: 'Snagg Pink',
  snaggPinkLight: 'Pink Light',
};

const COLOR_LABELS: { key: keyof ThemeColors; label: string }[] = [
  { key: 'primary', label: 'Primary' },
  { key: 'background', label: 'Background' },
  { key: 'surface', label: 'Surface' },
  { key: 'text', label: 'Text' },
  { key: 'border', label: 'Border' },
];

const HEX_REGEX = /^#[0-9A-Fa-f]{6}$/;

function themesEqual(a: ThemeColors, b: ThemeColors): boolean {
  return a.primary === b.primary
    && a.background === b.background
    && a.surface === b.surface
    && a.text === b.text
    && a.border === b.border;
}

function findPresetKey(theme: ThemeColors): string | null {
  for (const [key, preset] of Object.entries(THEME_PRESETS)) {
    if (themesEqual(theme, preset)) return key;
  }
  return null;
}

export function DisplayPage() {
  useDocumentTitle('Display');
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();

  const savedTheme = themeFromConfig(user?.themeConfig ?? null);
  const savedPresetKey = findPresetKey(savedTheme);

  const [selectedPreset, setSelectedPreset] = useState<string | null>(savedPresetKey);
  const [customColors, setCustomColors] = useState<ThemeColors>(
    savedPresetKey ? DEFAULT_THEME : savedTheme,
  );
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Track saved state to detect changes
  const savedThemeRef = useRef(savedTheme);

  // Current active theme for comparison
  const activeTheme: ThemeColors = selectedPreset
    ? THEME_PRESETS[selectedPreset]
    : customColors;

  const hasChanges = !themesEqual(activeTheme, savedThemeRef.current);

  // Revert on unmount if unsaved
  useEffect(() => {
    return () => {
      applyTheme(savedThemeRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePresetClick = (key: string) => {
    setSelectedPreset(key);
    setError(null);
    applyTheme(THEME_PRESETS[key]);
  };

  const handleCustomClick = () => {
    setSelectedPreset(null);
    setError(null);
    applyTheme(customColors);
  };

  const handleCustomColorChange = (field: keyof ThemeColors, value: string) => {
    const upper = value.toUpperCase();
    const next = { ...customColors, [field]: upper };
    setCustomColors(next);
    if (HEX_REGEX.test(upper)) {
      applyTheme(next);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    try {
      const themeJson = JSON.stringify(activeTheme);
      await updateProfile({ themeConfig: themeJson });
      savedThemeRef.current = activeTheme;
      await refreshUser();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save theme');
    } finally {
      setIsSaving(false);
    }
  };

  const isCustom = selectedPreset === null;

  return (
    <Layout>
      <div style={styles.container}>
        <div style={styles.content}>
          <button style={styles.backButton} onClick={() => navigate('/account')}>
            <ArrowLeft size={18} /> Back to Account
          </button>

          <h1 style={styles.title}>Display</h1>
          <p style={styles.subtitle}>Customize your dashboard theme and colors</p>

          {error && <div style={styles.error}>{error}</div>}

          {/* Preset grid */}
          <div style={styles.presetGrid}>
            {Object.entries(THEME_PRESETS).map(([key, preset]) => {
              const isActive = selectedPreset === key;
              return (
                <button
                  key={key}
                  style={{
                    ...styles.presetCard,
                    ...(isActive ? styles.presetCardActive : {}),
                  }}
                  onClick={() => handlePresetClick(key)}
                >
                  <div style={styles.presetPreview}>
                    <div style={{ ...styles.previewSwatch, backgroundColor: preset.primary }} />
                    <div style={{ ...styles.previewSwatch, backgroundColor: preset.background }} />
                    <div style={{ ...styles.previewSwatch, backgroundColor: preset.surface }} />
                    <div style={{ ...styles.previewSwatch, backgroundColor: preset.text }} />
                    <div style={{ ...styles.previewSwatch, backgroundColor: preset.border }} />
                  </div>
                  <div style={styles.presetLabel}>
                    {isActive && <Check size={14} color={colors.brand.blue} />}
                    <span>{PRESET_NAMES[key]}</span>
                  </div>
                </button>
              );
            })}

            {/* Custom card */}
            <button
              style={{
                ...styles.presetCard,
                ...(isCustom ? styles.presetCardActive : {}),
              }}
              onClick={handleCustomClick}
            >
              <div style={styles.presetPreview}>
                <div style={{ ...styles.previewSwatch, backgroundColor: customColors.primary }} />
                <div style={{ ...styles.previewSwatch, backgroundColor: customColors.background }} />
                <div style={{ ...styles.previewSwatch, backgroundColor: customColors.surface }} />
                <div style={{ ...styles.previewSwatch, backgroundColor: customColors.text }} />
                <div style={{ ...styles.previewSwatch, backgroundColor: customColors.border }} />
              </div>
              <div style={styles.presetLabel}>
                {isCustom && <Check size={14} color={colors.brand.blue} />}
                <span>Custom</span>
              </div>
            </button>
          </div>

          {/* Custom color inputs */}
          {isCustom && (
            <div style={styles.customPanel}>
              <h3 style={styles.customTitle}>Custom Colors</h3>
              {COLOR_LABELS.map(({ key, label }) => {
                const value = customColors[key];
                const isValid = HEX_REGEX.test(value);
                return (
                  <div key={key} style={styles.colorRow}>
                    <div
                      style={{
                        ...styles.colorSwatch,
                        backgroundColor: isValid ? value : '#ccc',
                      }}
                    />
                    <label style={styles.colorLabel}>{label}</label>
                    <input
                      style={{
                        ...styles.colorInput,
                        ...(isValid ? {} : styles.colorInputInvalid),
                      }}
                      value={value}
                      onChange={e => handleCustomColorChange(key, e.target.value)}
                      placeholder="#000000"
                      maxLength={7}
                    />
                  </div>
                );
              })}
            </div>
          )}

          {/* Save button */}
          {hasChanges && (
            <button
              style={{
                ...styles.saveButton,
                ...(isSaving ? { opacity: 0.6 } : {}),
              }}
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : 'Save Theme'}
            </button>
          )}
        </div>
      </div>
    </Layout>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: '24px',
  },
  content: {
    maxWidth: '600px',
    margin: '0 auto',
  },
  backButton: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: colors.gray[600],
    fontSize: 14,
    fontWeight: 500,
    padding: 0,
    marginBottom: 16,
  },
  title: {
    fontSize: 28,
    fontWeight: 700,
    color: colors.ink.black,
    marginBottom: 24,
  },
  subtitle: {
    fontSize: '14px',
    color: colors.gray[600],
    marginBottom: '24px',
  },
  error: {
    backgroundColor: '#FEE2E2',
    border: '1px solid #FECACA',
    borderRadius: '8px',
    padding: '12px',
    color: '#DC2626',
    fontSize: '14px',
    marginBottom: '16px',
  },
  presetGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '12px',
    marginBottom: '24px',
  },
  presetCard: {
    padding: '12px',
    backgroundColor: colors.white,
    borderWidth: '2px',
    borderStyle: 'solid',
    borderColor: colors.gray[200],
    borderRadius: '12px',
    cursor: 'pointer',
    textAlign: 'center' as const,
    transition: 'border-color 0.15s',
  },
  presetCardActive: {
    borderColor: colors.brand.blue,
  },
  presetPreview: {
    display: 'flex',
    gap: '3px',
    borderRadius: '6px',
    overflow: 'hidden',
    marginBottom: '8px',
    height: '32px',
  },
  previewSwatch: {
    flex: 1,
  },
  presetLabel: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '4px',
    fontSize: '13px',
    fontWeight: 500,
    color: colors.ink.black,
  },
  customPanel: {
    backgroundColor: colors.white,
    border: `1px solid ${colors.gray[300]}`,
    borderRadius: '12px',
    padding: '20px',
    marginBottom: '24px',
  },
  customTitle: {
    fontSize: '16px',
    fontWeight: 600,
    color: colors.ink.black,
    marginBottom: '16px',
  },
  colorRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '12px',
  },
  colorSwatch: {
    width: '28px',
    height: '28px',
    borderRadius: '6px',
    border: `1px solid ${colors.gray[300]}`,
    flexShrink: 0,
  },
  colorLabel: {
    fontSize: '14px',
    fontWeight: 500,
    color: colors.ink.black,
    width: '90px',
    flexShrink: 0,
  },
  colorInput: {
    flex: 1,
    padding: '8px 12px',
    fontSize: '14px',
    fontFamily: 'monospace',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: colors.gray[300],
    borderRadius: '8px',
    outline: 'none',
    color: colors.ink.black,
    backgroundColor: colors.white,
  },
  colorInputInvalid: {
    borderColor: '#DC2626',
  },
  saveButton: {
    width: '100%',
    padding: '14px',
    backgroundColor: colors.brand.blue,
    color: colors.white,
    border: 'none',
    borderRadius: '12px',
    fontSize: '16px',
    fontWeight: 600,
    cursor: 'pointer',
  },
};
